import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import {
  getTriggerOrderQuoteRequest,
  executeTriggerOrderQuoteRequest,
  cancelTriggerOrderRequest,
} from '../../request/triggerOrdersRequest';
import { getPortfolioTransactionSingle } from '../../request/portfolioRequests';
import {
  GET_TRIGGER_ORDER_QUOTE,
  GET_TRIGGER_ORDER_DETAILS,
  EXECUTE_TRIGGER_ORDER_QUOTE,
  CANCEL_TRIGGER_ORDER,
} from '../actions/triggerOrders/triggerOrdersActionConstants';
import {
  getTriggerOrderQuoteSuccess,
  getTriggerOrderQuoteError,
  getTriggerOrderDetailsSuccess,
  getTriggerOrderDetailsError,
  executeTriggerOrderQuoteSuccess,
  executeTriggerOrderQuoteError,
  cancelTriggerOrderSuccess,
  cancelTriggerOrderError,
} from '../actions/triggerOrders/triggerOrdersActions';
import { SINGLE_TRANSACTION_TYPE_TRIGGER_ORDER } from '../../util/enum/api/transactionTypes';
import { rejectErrorCodeHelper } from '../../util/helpers/rejectErrorCodeHelper';
import { normalizeSingleTransaction } from '../../util/helpers/transcation/singleTransactionHelper';

function* getTriggerOrderQuote({ payload }) {
  try {
    const { data } = yield call(getTriggerOrderQuoteRequest, payload);
    yield put(getTriggerOrderQuoteSuccess(data));
    if (payload?.onSuccess) {
      yield call(payload.onSuccess, data);
    }
  } catch (error) {
    const errorMessage = yield call(rejectErrorCodeHelper, error);
    yield put(getTriggerOrderQuoteError(errorMessage));
    if (payload?.onError) {
      yield call(payload.onError, errorMessage);
    }
  }
}

// The order's details come from the same single transaction API the
// transaction tables call (the TriggerOrderUid is the transaction uid, with
// ?type=TriggerOrder), but go to the caller through onSuccess only: they are not
// put in the singleTransaction slice the tables open TransactionDetails off.
// Both callers show the order's row when this fails, so it opts out of the 500
// redirect to the error page (skipInternalServerErrorRedirect, the flag
// internalServerErrorMiddleware reads; orderRequest.js sets it the same way):
// that route change closes the modal showing the fallback, and, right after an
// order was placed, would read as the order having failed.
function* getTriggerOrderDetails({ payload }) {
  try {
    const { data } = yield call(
      getPortfolioTransactionSingle,
      payload.accountUid,
      payload.triggerOrderUid,
      SINGLE_TRANSACTION_TYPE_TRIGGER_ORDER,
      { skipInternalServerErrorRedirect: true },
    );
    const details = normalizeSingleTransaction(data);
    yield put(getTriggerOrderDetailsSuccess(details));
    if (payload?.onSuccess) {
      yield call(payload.onSuccess, details);
    }
  } catch (error) {
    const errorMessage = yield call(rejectErrorCodeHelper, error);
    yield put(getTriggerOrderDetailsError(errorMessage));
    if (payload?.onError) {
      yield call(payload.onError, errorMessage);
    }
  }
}

function* executeTriggerOrderQuote({ payload }) {
  try {
    const { data } = yield call(executeTriggerOrderQuoteRequest, payload);
    yield put(executeTriggerOrderQuoteSuccess(data));
    if (payload?.onSuccess) {
      yield call(payload.onSuccess, data);
    }
  } catch (error) {
    const errorMessage = yield call(rejectErrorCodeHelper, error);
    yield put(executeTriggerOrderQuoteError(errorMessage));
    if (payload?.onError) {
      yield call(payload.onError, errorMessage);
    }
  }
}

function* cancelTriggerOrder({ payload }) {
  try {
    const { data } = yield call(cancelTriggerOrderRequest, payload);
    yield put(cancelTriggerOrderSuccess(data));
    if (payload?.onSuccess) {
      yield call(payload.onSuccess, data);
    }
  } catch (error) {
    const errorMessage = yield call(rejectErrorCodeHelper, error);
    yield put(cancelTriggerOrderError(errorMessage));
    if (payload?.onError) {
      yield call(payload.onError, errorMessage);
    }
  }
}

export default function* triggerOrdersSaga() {
  yield all([
    takeLatest(GET_TRIGGER_ORDER_QUOTE, getTriggerOrderQuote),
    takeLatest(GET_TRIGGER_ORDER_DETAILS, getTriggerOrderDetails),
    takeLatest(EXECUTE_TRIGGER_ORDER_QUOTE, executeTriggerOrderQuote),
    takeLatest(CANCEL_TRIGGER_ORDER, cancelTriggerOrder),
  ]);
}
